/*  MUI  */
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { SaaSHealthLineGraph } from 'application/Shared/Components/Reports/SaaSHealthLineGraph';
import { QBRFIRSTREPORTS } from 'application/Shared/Constants';
import { Route, dateTimeString } from 'application/Shared/Helpers';
import { Insights, Notes } from 'application/Shared/Pages';

/*  NPM  */
import { endOfQuarter, startOfQuarter } from 'date-fns';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import CoverPage from '../Components/Containers/CoverPage';
import { QBRFirstAppOverlapScore } from './internal/QBRFirstAppOverlapScore';
import { QBRFirstApplicationClassificationResults } from './internal/QBRFirstApplicationClassificationResults';
import { QBRFirstManagedAppScore } from './internal/QBRFirstManagedAppScore';
import { QBRFirstScoreOverview } from './internal/QBRFirstScoreOverview';
import { QBRFirstShadowItScore } from './internal/QBRFirstShadowItScore';
import { QBRFirstUnapprovedAppScore } from './internal/QBRFirstUnapprovedAppScore';
import { QBRScoreChip } from './internal/QBRScoreChip';

/*  Constants  */
const reportPrefix = 'qbrQBRFirst';
const scoreOverviewReport = `${reportPrefix}ScoreOverview`;

/*  Styles  */
const sxs = {
    default: { margin: { marginLeft: 0, marginRight: 0 } }
};

const RenderFirstScoreOverviewWithGraph = (props) => {
    const { t } = useTranslation();
    const { height, saasHealthLineGraphOnly } = props;

    return (
        <Grid
            container
            data-testid="qbrRenderFirstScoreOverviewWithGraph"
            spacing={3}
        >
            <Grid item xs={12} lg={saasHealthLineGraphOnly ? 12 : 8}>
                <Paper
                    variant="outlined"
                    style={{ padding: '16px' }}
                    data-testid="qbrRenderFirstScoreOverviewSaaSGraph"
                >
                    {saasHealthLineGraphOnly && (
                        <>
                            <Typography color="textSecondary" variant="h4">
                                {t('Common.health_score')}
                            </Typography>
                            <hr />
                        </>
                    )}
                    <SaaSHealthLineGraph
                        {...props}
                        height={saasHealthLineGraphOnly ? height : 462}
                    />
                </Paper>
            </Grid>
            {!saasHealthLineGraphOnly && (
                <Grid
                    item
                    xs={12}
                    lg={4}
                    data-testid="qbrRenderFirstScoreOverviewScores"
                >
                    <QBRFirstScoreOverview {...props} withGraph />
                </Grid>
            )}
        </Grid>
    );
};

const RenderScoreDetails = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { reportData } = props;

    const Scores = Object.keys(QBRFIRSTREPORTS).map((reportType) => {
        const reportKey = QBRFIRSTREPORTS[reportType].key;

        if (reportType === 'SaaSSophisticationScore') {
            return <span key="renderScoreDetails_SaaSSophisticationScore" />;
        }

        if (!reportData[reportKey]) {
            return null;
        }

        const score = reportData[reportKey].score || 0;

        return (
            <Box
                key={`renderScoreDetails_${reportType}`}
                data-testid="qbrRenderScoreDetails"
                display="flex"
                flexDirection="column"
            >
                <Typography
                    variant="p"
                    style={{
                        color: theme.palette.qbrFirstReports[reportType],
                        fontWeight: 600
                    }}
                >
                    {QBRFIRSTREPORTS[reportType].name}:
                    <QBRScoreChip score={score} />
                </Typography>
                <Typography variant="p">
                    {t(`QBRFirst.definitions.${reportType}`)}
                </Typography>
            </Box>
        );
    });

    return <Box m="32px">{Scores}</Box>;
};

const RenderInsights = (props) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const baseAdminRoute = Route.buildBaseAdminRoute({ clientId, partnerId });
    const { reportSetup } = props;
    const { endTime, period, startTime } = reportSetup;

    const start =
        period === 'quarterly'
            ? startOfQuarter(new Date(startTime))
            : new Date(startTime);
    const end =
        period === 'quarterly'
            ? endOfQuarter(new Date(endTime))
            : new Date(endTime);

    const dateSearch = {
        field: 'dateCreated',
        value: `{"between": "${dateTimeString(start)}, ${dateTimeString(end)}"}`
    };

    return (
        <Box
            p="32px"
            style={{ pageBreakBefore: 'always' }}
            data-testid="qbrRenderInsights"
        >
            <Typography variant="h1">{t('Common.insights')}</Typography>
            <Typography
                style={{
                    ...sxs.default.margin,
                    marginTop: '12px'
                }}
            >
                {t('QBR.definitions.insights')}
            </Typography>
            <hr />
            <Box
                style={{
                    ...sxs.default.margin
                }}
            >
                <Insights
                    report
                    dateSearch={dateSearch}
                    link={`${baseAdminRoute}/insights`}
                />
            </Box>
        </Box>
    );
};

const RenderNotes = (props) => {
    const { t } = useTranslation();
    const { reportSetup } = props;
    const { startTime } = reportSetup;

    const start = startOfQuarter(new Date(startTime));

    return (
        <Box
            p="32px"
            style={{ pageBreakBefore: 'always' }}
            data-testid="qbrRenderNotes"
        >
            <Typography variant="h1">
                {t('Common.notes_and_recommendations')}
            </Typography>
            <Typography
                style={{
                    ...sxs.default.margin,
                    marginTop: '12px'
                }}
            >
                {t('QBR.definitions.notes')}
            </Typography>
            <hr />
            <Box style={sxs.default.margin}>
                <Notes excludeCompletedBefore={dateTimeString(start)} report />
            </Box>
        </Box>
    );
};

export const QBRFirstReports = (props) => {
    const { reportSetup, reportData, reportWrapper, renderingScheduledReport } =
        props;

    const { t } = useTranslation();

    if (typeof reportData === 'undefined') {
        return null;
    }

    // This is for the score cards in the Admin Dashboard for the Client
    if (reportSetup?.reportSelections?.[scoreOverviewReport]) {
        return <QBRFirstScoreOverview {...props} />;
    }

    if (reportSetup?.reportSelections?.[`${scoreOverviewReport}AndGraph`]) {
        return <RenderFirstScoreOverviewWithGraph {...props} />;
    }

    const { reportSelections } = reportSetup;

    const data = [];
    const scoreMap = {};

    Object.entries(reportData).forEach(([key, value]) => {
        const reportType = key.replace(reportPrefix, '');
        data.push({
            name: QBRFIRSTREPORTS[reportType].name,
            score: value.score
        });
        scoreMap[key] = value.score;
    });

    return (
        <Box width="100" position="relative" data-testid="qbrMainRender">
            {reportSetup.reportSelections?.qbrQBRFirstCover && (
                <CoverPage {...props} />
            )}
            {reportSelections.qbrQBRFirstSaaSSophisticationScore && (
                <Box data-testid="qbrMainRenderSaaSHealthLineGraphWithScoreDetails">
                    <Box margin="32px">
                        <Box
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography variant="h1">
                                {QBRFIRSTREPORTS.SaaSSophisticationScore.name}:
                            </Typography>
                            <QBRScoreChip
                                score={
                                    scoreMap.qbrQBRFirstSaaSSophisticationScore
                                }
                                renderType="saasHealthHeader"
                            />
                        </Box>
                        {!reportWrapper && (
                            <Typography
                                style={{
                                    marginBottom: '32px',
                                    marginTop: '12px'
                                }}
                            >
                                {t(
                                    'QBRFirst.definitions.SaaSSophisticationScore'
                                )}
                            </Typography>
                        )}
                        <SaaSHealthLineGraph {...props} />
                    </Box>
                    <RenderScoreDetails {...props} />
                </Box>
            )}
            {reportSelections.qbrQBRFirstInsights && (
                <RenderInsights {...props} />
            )}
            {reportSelections.qbrQBRFirstNotes && <RenderNotes {...props} />}
            {reportData.qbrQBRFirstAppClassificationScore && (
                <Box
                    p="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstApplicationClassificationResults"
                >
                    <QBRFirstApplicationClassificationResults
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={
                            reportData.qbrQBRFirstAppClassificationScore
                        }
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstManagedAppScore && (
                <Box
                    p="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstManagedAppScore"
                >
                    <QBRFirstManagedAppScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstManagedAppScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstShadowITScore && (
                <Box
                    p="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstShadowItScore"
                >
                    <QBRFirstShadowItScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstShadowITScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstUnapprovedAppScore && (
                <Box
                    p="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstUnapprovedAppScore"
                >
                    <QBRFirstUnapprovedAppScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstUnapprovedAppScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstAppOverlapScore && (
                <Box
                    p="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstAppOverlapScore"
                >
                    <QBRFirstAppOverlapScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstAppOverlapScore}
                    />
                </Box>
            )}
        </Box>
    );
};
